import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import SuperAdminSidebar from '../../components/SuperAdmin/SuperAdminSidebar';
import '../../styles/Dashboard.css';
import '../../styles/SuperAdmin/PlansManagement.css';

const PlansManagement = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    assets: '',
    users: '',
    currency: 'USD',
    features: [],
    highlighted: false,
    cta: 'Contact Sales'
  });

  // Currency conversion rates (based on USD)
  const currencyRates = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    INR: 83.12,
    JPY: 149.50,
    AUD: 1.52,
    CAD: 1.36
  };

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$'
  };

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const token = sessionStorage.getItem('token');

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/plans`);
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      const data = await response.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchPlans();
  }, [user.role, navigate, fetchPlans]);

  const handleOpenModal = (plan = null) => {
    setError('');
    setSuccess('');
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description,
        price: plan.price,
        originalPrice: plan.originalPrice || '',
        assets: plan.assets,
        users: plan.users,
        currency: plan.currency || 'USD',
        features: plan.features || [],
        highlighted: plan.highlighted || false,
        cta: plan.cta || 'Contact Sales'
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        originalPrice: '',
        assets: '',
        users: '',
        currency: 'USD',
        features: [],
        highlighted: false,
        cta: 'Contact Sales'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addFeature = () => {
    const featureInput = document.getElementById('featureInput');
    const feature = featureInput.value.trim();
    
    if (feature && !formData.features.includes(feature)) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, feature]
      }));
      featureInput.value = '';
    }
  };

  const removeFeature = (idx) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!formData.name || !formData.description || !formData.price || !formData.assets || !formData.users || formData.features.length === 0) {
        setError('Please fill in all required fields');
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        originalPrice: formData.originalPrice ? Number(formData.originalPrice) : null,
        assets: Number(formData.assets),
        users: Number(formData.users),
        currency: formData.currency,
        features: formData.features,
        highlighted: formData.highlighted,
        cta: formData.cta
      };

      const url = editingPlan 
        ? `${apiUrl}/api/plans/${editingPlan._id}` 
        : `${apiUrl}/api/plans`;

      const method = editingPlan ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${editingPlan ? 'update' : 'create'} plan`);
      }

      setSuccess(`Plan ${editingPlan ? 'updated' : 'created'} successfully!`);
      
      // Refresh plans list
      await fetchPlans();
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.message || 'Failed to save plan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) {
      return;
    }

    try {
      setError('');
      const response = await fetch(`${apiUrl}/api/plans/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete plan');
      }

      setSuccess('Plan deleted successfully!');
      // Refresh plans list
      await fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      setError(error.message || 'Failed to delete plan');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <SuperAdminSidebar user={user} />
        <div className="dashboard-main">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '16px', color: '#666' }}>Loading plans...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <SuperAdminSidebar user={user} />
      
      <div className="dashboard-main">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: '20px', background: '#fee', border: '1px solid #fbcaca', borderRadius: '6px', color: '#c00', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ padding: '12px 16px', marginBottom: '20px', background: '#efe', border: '1px solid #bfb', borderRadius: '6px', color: '#060', fontSize: '14px' }}>
            {success}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', color: '#1a1f3a', fontSize: '28px', fontWeight: '700' }}>Plans Management</h1>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>Create and manage subscription plans for your customers</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Display Currency:</label>
              <select 
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
              >
                {Object.keys(currencySymbols).map(curr => (
                  <option key={curr} value={curr}>{curr} ({currencySymbols[curr]})</option>
                ))}
              </select>
            </div>
            <button 
              onClick={() => handleOpenModal()}
              style={{ padding: '10px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaPlus /> Create New Plan
            </button>
          </div>
        </div>

        <div className="plans-container">
          {plans.length === 0 ? (
            <div className="empty-state">
              <p>No plans created yet. Create your first plan!</p>
              <button 
                className="btn-primary"
                onClick={() => handleOpenModal()}
              >
                <FaPlus /> Create Plan
              </button>
            </div>
          ) : (
            <div className="plans-grid">
              {plans.map(plan => (
                <div key={plan._id} className={`plan-card ${plan.highlighted ? 'highlighted' : ''}`}>
                  {plan.highlighted && <div className="featured-badge">FEATURED</div>}
                  
                  <div className="plan-header">
                    <h3>{plan.name}</h3>
                    <p className="plan-description">{plan.description}</p>
                  </div>

                  <div className="plan-pricing">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {plan.originalPrice && (
                        <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '14px' }}>
                          {currencySymbols[selectedCurrency]}{(plan.originalPrice * currencyRates[selectedCurrency]).toFixed(2)}
                        </span>
                      )}
                      <span className="price">
                        {currencySymbols[selectedCurrency]}{(plan.price * currencyRates[selectedCurrency]).toFixed(2)}
                      </span>
                    </div>
                    <span className="cycle">/month ({plan.currency})</span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <div style={{ 
                      background: '#e8f4f8', 
                      padding: '4px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      color: '#0369a1',
                      fontWeight: '600'
                    }}>
                      📦 {plan.assets} Assets
                    </div>
                    <div style={{ 
                      background: '#f0e8f8', 
                      padding: '4px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      color: '#6f42c1',
                      fontWeight: '600'
                    }}>
                      👥 {plan.users} Users
                    </div>
                  </div>

                  <div className="plan-features">
                    <h4>Includes:</h4>
                    <ul>
                      {Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                        <li key={`${plan._id}-feature-${idx}`}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="plan-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleOpenModal(plan)}
                      title="Edit plan"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(plan._id)}
                      title="Delete plan"
                    >
                      <FaTrash />
                    </button>
                    <button 
                      className="btn-view"
                      title="Preview plan"
                    >
                      <FaEye />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Plan Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>×</button>
              </div>

              <form onSubmit={handleSubmit} className="plan-form">
                <div className="form-group">
                  <label>Plan Name *</label>
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Starter, Professional"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <input 
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g., Perfect for small teams"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Price ($) *</label>
                  <input 
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="99"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Original Price (for discount) - Optional</label>
                  <input 
                    type="number"
                    name="originalPrice"
                    value={formData.originalPrice}
                    onChange={handleInputChange}
                    placeholder="e.g., 149 (leave empty if no discount)"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Max Assets *</label>
                    <input 
                      type="number"
                      name="assets"
                      value={formData.assets}
                      onChange={handleInputChange}
                      placeholder="e.g., 10"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Max Users *</label>
                    <input 
                      type="number"
                      name="users"
                      value={formData.users}
                      onChange={handleInputChange}
                      placeholder="e.g., 5"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Currency *</label>
                  <select 
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="INR">INR (₹)</option>
                    <option value="JPY">JPY (¥)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>CTA Button Text</label>
                  <input 
                    type="text"
                    name="cta"
                    value={formData.cta}
                    onChange={handleInputChange}
                    placeholder="e.g., Contact Sales, Get Started"
                  />
                </div>

                <div className="form-group">
                  <label>Features (Add one at a time) *</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <input 
                      id="featureInput"
                      type="text"
                      placeholder="e.g., Email support"
                      style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                    <button 
                      type="button"
                      onClick={addFeature}
                      style={{ padding: '8px 16px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Add
                    </button>
                  </div>
                  {formData.features.length === 0 && (
                    <div style={{ padding: '8px 12px', background: '#fef3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404', fontSize: '13px' }}>
                      Please add at least one feature
                    </div>
                  )}
                  {formData.features.length > 0 && (
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                      {formData.features.map((feature, idx) => (
                        <div 
                          key={`feature-${idx}`}
                          style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '10px 12px',
                            borderBottom: idx < formData.features.length - 1 ? '1px solid #f0f0f0' : 'none',
                            background: '#f9f9f9'
                          }}
                        >
                          <span style={{ fontSize: '14px', color: '#333' }}>{feature}</span>
                          <button
                            type="button"
                            onClick={() => removeFeature(idx)}
                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input 
                      type="checkbox"
                      name="highlighted"
                      checked={formData.highlighted}
                      onChange={handleInputChange}
                    />
                    <span>Mark as Featured Plan</span>
                  </label>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={!formData.name || !formData.description || !formData.price || !formData.assets || !formData.users || formData.features.length === 0}
                    style={{
                      opacity: !formData.name || !formData.description || !formData.price || !formData.assets || !formData.users || formData.features.length === 0 ? 0.5 : 1,
                      cursor: !formData.name || !formData.description || !formData.price || !formData.assets || !formData.users || formData.features.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansManagement;
